/** Общие элементы интерфейса: уведомления, подтверждения, модальные окна, пустые состояния. */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import { useNavigate } from "react-router";

/* ================= Уведомления ================= */
type ToastKind = "ok" | "error" | "info";
interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
  action?: { label: string; to: string };
}
type ToastFn = (text: string, kind?: ToastKind, action?: ToastItem["action"]) => void;

const ToastCtx = createContext<ToastFn>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const navigate = useNavigate();
  const close = (id: number) => setItems((xs) => xs.filter((x) => x.id !== id));
  const toast = useCallback<ToastFn>((text, kind = "ok", action) => {
    const id = ++seq.current;
    setItems((xs) => [...xs.slice(-3), { id, text, kind, action }]);
    setTimeout(() => close(id), kind === "error" ? 9000 : action ? 8000 : 4500);
  }, []);
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <div key={t.id} className={`toast toast--${t.kind}`} role={t.kind === "error" ? "alert" : "status"}>
            {t.kind === "error" ? <CircleAlert size={18} /> : t.kind === "info" ? <Info size={18} /> : <CircleCheck size={18} />}
            <span className="toast__text">{t.text}</span>
            {t.action && (
              <button
                className="toast__action"
                onClick={() => {
                  navigate(t.action!.to);
                  close(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            <button className="toast__close" onClick={() => close(t.id)} aria-label="Закрыть уведомление">
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ================= Модальное окно ================= */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide = false,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  const titleId = labelledBy ?? "modal-title";
  // в body: окно из скрытой вкладки (редактор на телефоне) иначе не показалось бы
  return createPortal(
    <dialog
      ref={ref}
      className={`modal ${wide ? "modal--wide" : ""}`}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {open && (
        <div className="modal__box">
          <header className="modal__head">
            <h3 id={titleId}>{title}</h3>
            <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
              <X size={20} />
            </button>
          </header>
          <div className="modal__body">{children}</div>
          {footer && <footer className="modal__foot">{footer}</footer>}
        </div>
      )}
    </dialog>,
    document.body,
  );
}

/* ================= Подтверждение ================= */
interface ConfirmOpts {
  title: string;
  text?: ReactNode;
  confirm: string;
  danger?: boolean;
}
type ConfirmFn = (o: ConfirmOpts) => Promise<boolean>;
const ConfirmCtx = createContext<ConfirmFn>(async () => false);
export const useConfirm = () => useContext(ConfirmCtx);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const ask = useCallback<ConfirmFn>((o) => new Promise((resolve) => setState({ ...o, resolve })), []);
  const done = (v: boolean) => {
    state?.resolve(v);
    setState(null);
  };
  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      <Modal
        open={!!state}
        onClose={() => done(false)}
        title={state?.title ?? ""}
        labelledBy="confirm-title"
        footer={
          <>
            <button className="btn btn--ghost" onClick={() => done(false)}>
              Отмена
            </button>
            <button className={`btn ${state?.danger ? "btn--primary" : "btn--dark"}`} onClick={() => done(true)} autoFocus>
              {state?.confirm}
            </button>
          </>
        }
      >
        {state?.text && <div className="confirm-text">{state.text}</div>}
      </Modal>
    </ConfirmCtx.Provider>
  );
}

/* ================= Маскоты (PNG владельца, brand/mascots) ================= */
/** Настроение → маскот: машет, обнимает сердце, грустит, думает. */
export const MASCOT = {
  wave: "/brand/mascots/mascot-2.png",
  hug: "/brand/mascots/mascot-1.png",
  sad: "/brand/mascots/mascot-3.png",
  think: "/brand/mascots/mascot-8.png",
} as const;
export type MascotMood = keyof typeof MASCOT;

/* ================= Пустое состояние ================= */
export function EmptyState({
  title,
  text,
  action,
  mascot = "wave",
  compact = false,
}: {
  title: string;
  text?: ReactNode;
  action?: ReactNode;
  mascot?: MascotMood | "none";
  compact?: boolean;
}) {
  return (
    <div className={`empty ${compact ? "empty--compact" : ""}`}>
      {mascot !== "none" && <img className={`empty__mascot empty__mascot--${mascot}`} src={MASCOT[mascot]} alt="" />}
      <div className="empty__body">
        <h3>{title}</h3>
        {text && <p className="muted">{text}</p>}
        {action && <div className="empty__action">{action}</div>}
      </div>
    </div>
  );
}

/* ================= Заголовок страницы ================= */
export function PageHeader({ title, lead, actions }: { title: ReactNode; lead?: ReactNode; actions?: ReactNode }) {
  useEffect(() => {
    if (typeof title === "string") document.title = `${title} — Импульс · Контент-студия`;
  }, [title]);
  return (
    <header className="page-head">
      <div className="page-head__text">
        <h1>{title}</h1>
        {lead && <p className="page-head__lead">{lead}</p>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </header>
  );
}

/** Ошибка загрузки раздела — с маскотом и подсказкой. */
export function LoadError({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <EmptyState
      mascot="sad"
      title="Не получилось загрузить"
      text={error instanceof Error ? error.message : String(error)}
      action={
        retry && (
          <button className="btn" onClick={retry}>
            Повторить
          </button>
        )
      }
    />
  );
}
